/**
 * PRICE-CUST-1 · 客户专属价 · 真契约源 apps/api-server/src/api_server/routes/pricing_customer_prices.py
 * （5 层取价：L5 客户×产品 → L4 客户全部产品 → L3 客群 → L2 单产品×渠道 → L1 全局，越具体越优先、只取一层）
 */
import { api } from "./api";

const BASE = "/api/v1/pricing/customer-prices";

export interface CustomerPartnerItem {
  id: string;
  code: string;
  name_cn: string;
  tier_code: string | null;
  override_count: number;   // 已设专属价条数（全部产品系数 + 客户×产品）
}

export interface PriceSource {
  level: 1 | 2 | 3 | 4 | 5;
  label: string;            // 客户专属·固定价 / 客户专属 +12% / 客户整体 −3% / 客群 B 级 +5% / 本产品单独设… / 跟随全局
}

export interface CustomerDefault {
  mode: "markup";
  markup_pct: number;       // 在结算价上整体 ±%
  effective_from: string | null;
  effective_to: string | null;
  active: boolean;
}

export interface CustomerProductRow {
  product_id: string;
  code: string;
  title: string;
  cost_cents: number;
  settlement_cents: number;
  retail_cents: number;
  source: PriceSource;
  override_id: string | null;
  below_cost: boolean;
}

export interface CustomerPartnerSummary {
  id: string;
  code: string;
  name_cn: string;
  tier_code: string | null;
  tier_name: string | null;
  tier_markup_pct: number | null;
  settlement_mode: string;
  settlement_cycle: string;
  override_count: number;
}

export interface CustomerPricesOverview {
  partner: CustomerPartnerSummary;
  default: CustomerDefault | null;
  occupancy: number;
  products: CustomerProductRow[];
}

export interface CustomerOverrideValue {
  mode: "markup" | "fixed";
  markup_pct: number | null;
  fixed_price_cents: number | null;
  fixed_tier_prices: Record<string, number | null> | null;
  retail_override_cents: number | null;
  retail_tier_prices?: Record<string, number | null> | null;   // PRICE-LIST-TIER-1 · 多日团建议售价按档
  child_fixed_price_cents?: number | null;   // TICKETOPT-1 · 门票儿童结算价 / 人
  effective_from: string | null;
  effective_to: string | null;
}

export interface CustomerPriceDetail {
  partner_id: string;
  partner_name: string;
  product_id: string;
  code: string;
  title: string;
  occupancies: number[];
  tiers: Record<string, { cost_cents: number; settlement_cents: number; fallback_settlement_cents: number; fallback_source: PriceSource }>;
  override: CustomerOverrideValue | null;
  override_id: string | null;
  retail_cents: number;
  /** TICKETOPT-1 · 门票产品、在售票种里最低的儿童成本；有值才显示「儿童」一格 */
  child_cost_cents?: number | null;
}

export interface ReasonFields { reason_code?: string; reason?: string }

export interface DateWindow { effective_from?: string | null; effective_to?: string | null }

export interface CustomerProductBody extends ReasonFields, DateWindow {
  mode: "markup" | "fixed";
  markup_pct?: number | null;
  fixed_price_cents?: number | null;
  fixed_tier_prices?: Record<string, number | null> | null;
  retail_override_cents?: number | null;
  retail_tier_prices?: Record<string, number | null> | null;
  child_fixed_price_cents?: number | null;   // TICKETOPT-1
}

export const listCustomerPartners = (q?: string) =>
  api<CustomerPartnerItem[]>(`${BASE}/partners${q ? `?q=${encodeURIComponent(q)}` : ""}`);

export const getCustomerPrices = (partnerId: string, occupancy = 2) =>
  api<CustomerPricesOverview>(`${BASE}?partner_id=${partnerId}&occupancy=${occupancy}`);

export const getCustomerPriceDetail = (partnerId: string, productId: string) =>
  api<CustomerPriceDetail>(`${BASE}/${partnerId}/products/${productId}`);

/** 设 / 清「这个客户全部产品」系数；markup_pct = null → 跟随客群 */
export const putCustomerDefault = (partnerId: string, body: { markup_pct: number | null } & ReasonFields & DateWindow) =>
  api<{ partner_id: string; default: CustomerDefault | null }>(`${BASE}/${partnerId}/default`, { method: "PUT", body: JSON.stringify(body) });

export const putCustomerProduct = (partnerId: string, productId: string, body: CustomerProductBody) =>
  api<CustomerPriceDetail>(`${BASE}/${partnerId}/products/${productId}`, { method: "PUT", body: JSON.stringify(body) });

export const deleteCustomerProduct = (partnerId: string, productId: string, reason?: ReasonFields) => {
  const qs = new URLSearchParams();
  if (reason?.reason_code) qs.set("reason_code", reason.reason_code);
  if (reason?.reason) qs.set("reason", reason.reason);
  return api<void>(`${BASE}/${partnerId}/products/${productId}${qs.toString() ? `?${qs}` : ""}`, { method: "DELETE" });
};

export const batchCustomerProducts = (
  partnerId: string,
  body: { product_ids: string[]; mode: "markup" | "reset"; markup_pct?: number | null } & ReasonFields & DateWindow,
) => api<{ partner_id: string; changed: number; requested: number }>(`${BASE}/${partnerId}/products:batch`, { method: "POST", body: JSON.stringify(body) });

/** 来源标签的颜色（样稿：5 紫 / 4 蓝 / 3 绿 / 2 琥珀 / 1 灰） */
export const SOURCE_TONE: Record<number, string> = {
  5: "bg-violet-50 text-violet-700",
  4: "bg-blue-50 text-blue-700",
  3: "bg-emerald-50 text-emerald-700",
  2: "bg-amber-50 text-amber-700",
  1: "bg-slate-100 text-slate-600",
};
